import type { PayloadAction, SerializedError } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type {
  AugmentedPhoneExtension,
  SubscriptionState,
  SubscriptionStatus,
} from '../services/contactsService';
import * as contactsService from '../services/contactsService';
import type { ThunkAPI } from '../store';
import { finaliseLogout } from '../store/thunks';
import {
  ApiPhonebookEntry,
  ApiPhonebookEntryCreate,
} from '../types/PhonebookEntry';
import { ApiPhoneExtension } from '../types/PhoneExtension';
import { ApiVirtualExtension } from '../types/VirtualExtension';

export type ContactsFilter = 'SHOW_ALL' | 'SHOW_PHONEBOOK' | 'SHOW_ENDPOINTS';

const warnMissingCustomerId = (action: string) => {
  console.warn(`No customer Id defined when attempting to ${action}`);
};

const combineArrays = <T extends { id: string } & SubscriptionState>(
  oldArr: T[],
  newArr: T[],
): T[] => {
  return newArr.map((newItem) => {
    const oldItem = oldArr.find((o) => o.id === newItem.id);
    return !oldItem
      ? newItem
      : {
          ...newItem,
          status: oldItem.status,
          subscribed: oldItem.subscribed,
          subscription: oldItem.subscription,
        };
  });
};

type FetchResult = {
  phonebook: ApiPhonebookEntry[];
  phones: AugmentedPhoneExtension[];
  virtuals: ApiVirtualExtension[];
};

export interface ContactsState {
  isFetching: boolean;
  isSaving: boolean;
  error?: SerializedError;
  phonebook: ApiPhonebookEntry[];
  phones: AugmentedPhoneExtension[];
  virtuals: ApiVirtualExtension[];
  visibilityFilter: ContactsFilter;
  searchString: string;
}

const initialState: ContactsState = {
  isFetching: false,
  isSaving: false,
  error: undefined,
  phonebook: [],
  phones: [],
  virtuals: [],
  visibilityFilter: 'SHOW_ENDPOINTS',
  searchString: '',
};

export const fetchContacts = createAsyncThunk<FetchResult, void, ThunkAPI>(
  'contacts/fetch',
  async (_, thunkAPI) => {
    const customerId = thunkAPI.getState().client.activeCustomer?.id;

    if (!customerId) {
      warnMissingCustomerId('fetch contacts');
      return { phonebook: [], virtuals: [], phones: [] };
    }

    const [phonebook, virtuals, phones] = await Promise.all([
      contactsService.getPhonebook(customerId),
      contactsService.getVirtualExtensions(customerId),
      contactsService.getPhoneExtensions(customerId),
    ]);

    // if (appConfig.extension) {
    // FIXME ????
    // subscribePhones();
    // }

    return { phonebook, virtuals, phones };
  },
);

export const createPhonebookEntry = createAsyncThunk<
  ApiPhonebookEntry | undefined,
  ApiPhonebookEntryCreate,
  ThunkAPI
>('contacts/createPhonebookEntry', async (params, thunkAPI) => {
  const customerId = thunkAPI.getState().client.activeCustomer?.id;

  if (!customerId) {
    warnMissingCustomerId('create phonebook entry');
    return undefined;
  }

  const res = await contactsService.createPhonebookEntry(customerId, params);
  return res;
});

export const deletePhonebookEntry = createAsyncThunk<
  string | undefined,
  string,
  ThunkAPI
>('contacts/deletePhonebookEntry', async (entryId, thunkAPI) => {
  const customerId = thunkAPI.getState().client.activeCustomer?.id;

  if (!customerId) {
    warnMissingCustomerId('delete phonebook entry');
    return undefined;
  }

  await contactsService.deletePhonebookEntry(customerId, entryId);
  return entryId;
});

export const editPhonebookEntry = createAsyncThunk<
  ApiPhonebookEntry | undefined,
  { id: string; entry: ApiPhonebookEntryCreate },
  ThunkAPI
>('contacts/editPhonebookEntry', async (params, thunkAPI) => {
  const customerId = thunkAPI.getState().client.activeCustomer?.id;

  if (!customerId) {
    warnMissingCustomerId('edit phonebook entry');
    return undefined;
  }

  const res = await contactsService.editPhonebookEntry(
    customerId,
    params.id,
    params.entry,
  );
  return res;
});

export const editPhoneExtension = createAsyncThunk<
  ApiPhoneExtension | undefined,
  ApiPhoneExtension,
  ThunkAPI
>('contacts/editPhoneExtension', async (params, thunkAPI) => {
  const customerId = thunkAPI.getState().client.activeCustomer?.id;

  if (!customerId) {
    warnMissingCustomerId('edit phone extension');
    return undefined;
  }

  const res = await contactsService.editPhoneExtension(customerId, params);
  return res;
});

const contactsSlice = createSlice({
  name: 'contacts',
  initialState,
  reducers: {
    visibilityFilterSet: (
      state,
      action: PayloadAction<{
        filter: ContactsFilter;
      }>,
    ) => {
      state.visibilityFilter = action.payload.filter;
    },
    // FIXME those names...
    contactsSearched: (
      state,
      action: PayloadAction<{ searchString: string }>,
    ) => {
      state.searchString = action.payload.searchString;
    },
    phoneStatusSet: (
      state,
      action: PayloadAction<{ id: string; status: SubscriptionStatus }>,
    ) => {
      const { id, status } = action.payload;

      state.phones = [...state.phones].map((p) =>
        p.username === id ? { ...p, status, subscribed: true } : p,
      );
    },
    phonesUnsubscribed: (state) => {
      state.phones = [...state.phones].map((p) => ({
        ...p,
        subscribed: false,
      }));
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchContacts.pending, (state) => {
        state.isFetching = true;
      })
      .addCase(fetchContacts.rejected, (state, action) => {
        state.isFetching = false;
        state.error = action.error;
      })
      .addCase(
        fetchContacts.fulfilled,
        (state, action: PayloadAction<FetchResult>) => {
          const { phonebook = [], phones = [], virtuals = [] } = action.payload;

          const {
            phonebook: prevPhonebook,
            phones: prevPhones,
            virtuals: prevVirtuals,
          } = state;

          const combinedPhonebook = combineArrays(prevPhonebook, phonebook);
          const combinedPhones = combineArrays(prevPhones, phones);
          const combinedVirtuals = combineArrays(prevVirtuals, virtuals);

          state.phonebook = combinedPhonebook;
          state.phones = combinedPhones;
          state.virtuals = combinedVirtuals;
          state.isFetching = false;
          state.error = undefined;
        },
      )
      .addCase(createPhonebookEntry.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(createPhonebookEntry.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.error;
      })
      .addCase(
        createPhonebookEntry.fulfilled,
        (state, action: PayloadAction<ApiPhonebookEntry | undefined>) => {
          const entry = action.payload;

          if (!entry) {
            return;
          }

          state.phonebook.push(entry);
          state.isSaving = false;
          state.error = undefined;
        },
      )
      .addCase(deletePhonebookEntry.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(deletePhonebookEntry.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.error;
      })
      .addCase(
        deletePhonebookEntry.fulfilled,
        (state, action: PayloadAction<string | undefined>) => {
          const entryId = action.payload;

          if (!entryId) {
            return;
          }

          state.phonebook = state.phonebook.filter((p) => p.id !== entryId);
          state.isSaving = false;
          state.error = undefined;
        },
      )
      .addCase(editPhonebookEntry.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(editPhonebookEntry.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.error;
      })
      .addCase(
        editPhonebookEntry.fulfilled,
        (state, action: PayloadAction<ApiPhonebookEntry | undefined>) => {
          const entry = action.payload;

          if (!entry) {
            return;
          }

          const idx = state.phonebook.findIndex((p) => p.id === entry.id);
          if (idx < 0) {
            return;
          }
          state.phonebook[idx] = entry;
          state.isSaving = false;
          state.error = undefined;
        },
      )
      .addCase(editPhoneExtension.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(editPhoneExtension.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.error;
      })
      .addCase(
        editPhoneExtension.fulfilled,
        (state, action: PayloadAction<ApiPhoneExtension | undefined>) => {
          const entry = action.payload;

          if (!entry) {
            return;
          }

          const idx = state.phones.findIndex((p) => p.id === entry.id);
          if (idx < 0) {
            return;
          }
          state.phones[idx] = { ...state.phones[idx], ...entry };
          state.isSaving = false;
          state.error = undefined;
        },
      )
      .addCase(finaliseLogout.fulfilled, () => initialState);
  },
});

export const {
  contactsSearched,
  visibilityFilterSet,
  phoneStatusSet,
  phonesUnsubscribed,
} = contactsSlice.actions;

export default contactsSlice.reducer;
